import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import Logger from "@/utility/logger";

import "./DialogPopup.css";

import QualityCheckDisplay from "@/components/QualityCheckDisplay";
import { useFontStyle } from "@/context/StyleContext";
import DialogPopupHeader from "./DialogPopupHeader/DialogPopupHeader";
import DialogPopupFooter from "./DialogPopupFooter/DialogPopupFooter";
import DialogPopupToggleOptions from "./DialogPopupToggleOptions/DialogPopupToggleOptions";
import DialogPopupOptions from "./DialogPopupOptions/DialogPopupOptions";
import DialopPopupCustomInstructions from "./DialogPopupCustomInstructions/DialogPopupCustomInstructions";
import DialogPopupFeedback from "./DialogPopupFeedback/DialogPopupFeedback";
import DialogPopupProjectDocuments from "./DialogPopupProjectDocuments/DialogPopupProjectDocuments";
import DialogPopupSourceDocuments from "./DialogPopupSourceDocuments/DialogPopupSourceDocuments";
import { SyncOutlined, CommentOutlined, ReadOutlined, FileDoneOutlined } from "@ant-design/icons";
import { defaultFeedbackContent } from "@/lib/defaults";
import { downloadAndOpenBase64File } from "@/utility/documentDownload";
import { getDocumentsNotReadyPayload } from "@/utility/documentReadiness";
import { prepareSectionContentForInsertion } from "@/utility/prepareSectionContentForInsertion";
import { renderSectionContent } from "@/utility/renderSectionContent";
import {
  appendTaskUpdateIfNew,
  createActiveSectionTask,
  getSectionActiveTask,
  isRecoverableTaskInterruption,
  markSectionRegenerationStarted,
  releaseSectionTaskOnFailure,
} from "@/utility/sectionTaskState";
import { createMissingSectionBookmarkError, isMissingSectionBookmarkError } from "@/utility/wordBookmarkErrors";
import { currentDocumentUploadName } from "@/utility/currentDocumentUpload";
import { message } from "antd";

interface ToggleOption {
  id: string;
  label: string;
  states: [string, string];
  currentState: 0 | 1;
}

const formatRegenerationOptionInstruction = (option: string) => {
  if (option === "Use bullet points") {
    return "BULLET POINTS: Structure the output with clear bullet points instead of prose paragraphs.";
  } else if (option === "Less Detail") {
    return "DETAIL LEVEL: Reduce the level of detail and keep only the essential points.";
  } else if (option === "More Detail") {
    return "DETAIL LEVEL: Increase the level of detail with useful specifics and supporting context.";
  } else if (option === "Less Formal") {
    return "FORMALITY: Use plain, conversational professional language.";
  } else if (option === "More Formal") {
    return "FORMALITY: Use a polished, formal professional tone.";
  } else if (option === "Less Verbose") {
    return "VERBOSITY: Be concise, remove repetition, and avoid unnecessary explanation.";
  } else if (option === "More Verbose") {
    return "VERBOSITY: Expand the response with fuller explanations and helpful context.";
  } else {
    return `${option.toUpperCase()}: Apply this instruction directly to the output.`;
  }
};

const buildRegenerationInstructionBlock = (freeform: string, options: string[]) => {
  const instructions: string[] = [];
  const formattedFreeform = freeform.trim();

  if (formattedFreeform) {
    instructions.push(`IMPORTANT: Apply these instructions output: ${formattedFreeform}`);
  }

  options.forEach((option) => {
    const formattedOption = option.trim();

    if (formattedOption) {
      instructions.push(formatRegenerationOptionInstruction(formattedOption));
    }
  });

  if (instructions.length === 0) {
    return "";
  }

  return [
    `IMPORTANT Instructions!`,
    `Apply ALL of the following instructions exactly as specified.`,
    `These instructions take HIGHEST PRIORITY and override any conflicting style, formatting, tone, length, or structure directives from the original prompt.`,
    ``,
    ...instructions.map((instruction, i) => `${i + 1}. ${instruction}`),
    ``,
    `RULES:`,
    `- Apply every numbered instruction above without exception.`,
    `- Do NOT reference, quote, or acknowledge these instructions in your output.`,
    `- Do NOT add meta-commentary such as "Here is the revised version" or "As requested".`,
  ].join("\n");
};

function DialogPopup({
  selectedProject,
  sourceDialogOpen,
  setSourceDialogOpen,
  sourceDialogContent,
  setSourceDialogContent,
  sectionDialogContent,
  feedbackDialogOpen,
  setFeedbackDialogOpen,
  feedbackDialogContent,
  setFeedbackDialogContent,
  regenerateDialogOpen,
  setRegenerateDialogOpen,
  regenerateDialogContent,
  setRegenerateDialogContent,
  regenerateLoading,
  setRegenerateLoading,
  qualityCheckDialogOpen,
  setQualityCheckDialogOpen,
  qualityCheckDialogContent,
  // setQualityCheckDialogContent,
  toggleRegenerateOptions,
  // insertTextAtSection,
  parseMarkdownSections,
  insertAtSection,
  hasSectionBookmarks,
  // createTable,
  generatedSections,
  setGeneratedSections,
  setOutputDecision,
  setSectionEvalStatus,
  setSelectedSections,
  declineChanges,
  retrieveSourceDocument,
  retrievellmOuputWOGroupAsync,
  retreiveSourceDocumentMetadata,
  deleteDocument,
  deleteTag,
  deleteTagsFiles,
  setShowError,
  titleMap,
  setTest,
  location,
  retrieveAgentQuery,
  handleAgentStream,
  handleAIOutput,
  setIsAgentSectionRegenerationOn,
  agenticRegenerationRequest,
  setAgenticRegenerationRequest,
  setSectionGeneration,
  submitLangfuseFeedback,
  handleCurrentDocxUpload,
  retrieveDocumentsForImageInsertion,
  retrieveDocumentImages,
  downloadDocumentImage,
  setFeedbackStates,
  setSectionStatus,
  logAuditAction,
  reportTemplateProjectId,
  outputDecision,
  setDocumentReadinessNotice,
  clearDocumentReadinessNotice,
  scrollToGeneratedSection,
  clearGroundingForSection,
  tryClaimAgenticRegenerationRequest,
  releaseAgenticRegenerationRequest,
  insertionLockRef,
}) {
  const [toggleOptions, setToggleOptions] = React.useState<ToggleOption[]>([
    {
      id: "detail",
      label: "Detail Level",
      states: ["Less Detail", "More Detail"],
      currentState: 1,
    },
    {
      id: "tone",
      label: "Formality",
      states: ["Less Formal", "More Formal"],
      currentState: 1,
    },
    {
      id: "length",
      label: "Verbosity",
      states: ["Less Verbose", "More Verbose"],
      currentState: 0,
    },
  ]);

  const releaseAgenticRegenerationFailure = (section: any, errorMessage: string, taskId?: string | null) => {
    if (!section?.id) return;

    setGeneratedSections((prevSections) =>
      prevSections.map((generatedSection) =>
        generatedSection.id === section.id
          ? releaseSectionTaskOnFailure(generatedSection, errorMessage, taskId)
          : generatedSection
      )
    );

    setOutputDecision((prev) => ({
      ...prev,
      [section.id]: true,
    }));
  };

  const [expandedSources, setExpandedSources] = useState<Set<number>>(new Set());
  const [copiedSource, setCopiedSource] = useState<number | null>(null);
  const [dialogPopupTitle, setDialogPopupTitle] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const { draftStyles } = useFontStyle();

  const appendChatAsyncUpdate = (section, data) => {
    const status = data?.status || "running";
    const message =
      data?.message || (status?.toLowerCase() === "completed" ? "Task complete." : data?.chat_task_status || status);
    if (!section?.id || !message) return;

    const newUpdate = {
      message,
      status,
      task_id: data?.task_id,
      chat_task_status: data?.chat_task_status,
      event_seq: data?.event_seq,
      updated_at: data?.updated_at,
    };

    setGeneratedSections((prevSections) =>
      prevSections.map((generatedSection) => {
        if (generatedSection.id !== section.id) return generatedSection;

        const updates = generatedSection.agentUpdates || [];
        const lastUpdate = updates[updates.length - 1];
        if (
          (newUpdate.event_seq && lastUpdate?.event_seq === newUpdate.event_seq) ||
          (!newUpdate.event_seq && lastUpdate?.message === newUpdate.message && lastUpdate?.status === newUpdate.status)
        ) {
          return generatedSection;
        }

        const nextUpdates = appendTaskUpdateIfNew(updates, newUpdate);
        if (nextUpdates === updates) {
          return generatedSection;
        }

        return {
          ...generatedSection,
          agentUpdates: nextUpdates,
          chat_async_task_id: data?.task_id || generatedSection.chat_async_task_id,
          activeSectionTask:
            createActiveSectionTask(
              "chat_async",
              generatedSection.activeSectionTask?.mode ||
                (generatedSection.chatAsyncRegenerationInProgress ? "regeneration" : "generation"),
              data?.task_id || generatedSection.activeSectionTask?.taskId || generatedSection.chat_async_task_id
            ) || generatedSection.activeSectionTask,
        };
      })
    );
  };

  const markChatAsyncRegenerationStarted = (section) => {
    if (!section?.id) return;

    setSectionEvalStatus((prev) => ({
      ...prev,
      [section.id]: {
        ...prev[section.id],
        multimodel: {
          status: "uninitialized",
          data: null,
        },
      },
    }));

    setGeneratedSections((prevSections) =>
      prevSections.map((generatedSection) =>
        generatedSection.id === section.id
          ? {
              ...markSectionRegenerationStarted(generatedSection, "chat_async"),
              trace_url: null,
              chat_async_task_id: generatedSection.chat_async_task_id || null,
              activeSectionTask: undefined,
              evalStatus: undefined,
            }
          : generatedSection
      )
    );
  };

  const markChatAsyncRegenerationFinished = (section) => {
    if (!section?.id) return;
    setGeneratedSections((prevSections) =>
      prevSections.map((generatedSection) =>
        generatedSection.id === section.id
          ? {
              ...generatedSection,
              chatAsyncInProgress: false,
              chatAsyncRegenerationInProgress: false,
              agenticRegenerationInProgress: false,
              activeSectionTask: undefined,
            }
          : generatedSection
      )
    );
  };

  useEffect(() => {
    const requestId = agenticRegenerationRequest?.requestId;
    const requestSection = agenticRegenerationRequest?.section;

    const agentSectionRegeneration = async () => {
      const didClaimRegenerationRequest =
        typeof tryClaimAgenticRegenerationRequest === "function"
          ? tryClaimAgenticRegenerationRequest(requestId)
          : true;

      if (!didClaimRegenerationRequest) {
        Logger.debug("DialogPopup.tsx: Skipping duplicate agentic regeneration request:", requestId);
        return;
      }

      try {
        setSectionGeneration(true);
        await handleSectionRegeneration(requestSection);
      } catch (error) {
        Logger.error("DialogPopup.tsx: Failed to regenerate agent section:", error);
      } finally {
        releaseAgenticRegenerationRequest(requestId);
        setSectionGeneration(false);
        setIsAgentSectionRegenerationOn(false);
        setAgenticRegenerationRequest(null);
      }
    };

    if (requestSection?.id) {
      agentSectionRegeneration();
    }
  }, [agenticRegenerationRequest?.requestId]);

  useEffect(() => {
    if (sourceDialogOpen) {
      setDialogPopupTitle("Sources");
    } else if (feedbackDialogOpen) {
      setDialogPopupTitle("Feedback");
    } else if (regenerateDialogOpen) {
      setDialogPopupTitle("Regenerate Section");
    } else if (qualityCheckDialogOpen) {
      setDialogPopupTitle("Quality Assessment");
    }
  }, [sourceDialogOpen, feedbackDialogOpen, regenerateDialogOpen, qualityCheckDialogOpen]);

  // Handles toggling of regeneration options
  const onChangeToggleState = (element: ToggleOption, newState: 0 | 1) => {
    const previousState = element.currentState;
    toggleRegenerateOptions(element.states[newState]);
    setToggleOptions((prevOptions) =>
      prevOptions.map((option) => (option.label === element.label ? { ...option, currentState: newState } : option))
    );
    toggleRegenerateOptions(element.states[previousState]);
  };

  // Options for regeneration dialog
  const checkOptions = [
    // { name: "Simplify the language", type: "check" },
    { name: "Use bullet points", type: "check" },
  ];

  const deletePersistedFeedback = async (chat_history_id: string) => {
    if (!chat_history_id) return;

    setGeneratedSections((prevSections) =>
      prevSections.map((section) => {
        if (section.chat_history_id === chat_history_id) {
          const newSection = { ...section };
          delete newSection.feedback;
          return newSection;
        }
        return section;
      })
    );
  };

  // Handles the logic for submitting feedback
  const handleDelete = async () => {
    try {
      const section = feedbackDialogContent.section;
      const feedbackDialogContentText = feedbackDialogContent?.feedback?.trim()
        ? feedbackDialogContent?.feedback
        : "No feedback comment received";

      const deleteSectionFeedback = "Jenly-Add-in.Delete-Dialog.User-Feedback: " + feedbackDialogContentText;

      if (!section.fromReportDownload) {
        try {
          await submitLangfuseFeedback(feedbackDialogContent?.section?.chat_history_id, 1, deleteSectionFeedback);
        } catch (error) {
          Logger.error("DialogPopup.tsx: Error submitting feedback", error);
        }
      }

      await declineChanges(section.insertion_key);
      if (!section.fromReportDownload) {
        await deletePersistedFeedback(feedbackDialogContent?.section?.chat_history_id);
        await deleteTagsFiles(section.id);
      }
      clearGroundingForSection(section);

      setFeedbackStates((prev) => {
        const newStates = { ...prev };
        delete newStates[section.id];
        return newStates;
      });

      setGeneratedSections((prev) => prev.filter((s) => s.id !== section.id));

      setOutputDecision((prev) => {
        const newOutput = { ...prev };
        delete newOutput[section.id];
        return newOutput;
      });

      setSectionEvalStatus((prev) => {
        const newOutput = { ...prev };
        delete newOutput[section.id];
        return newOutput;
      });

      setFeedbackDialogOpen(false);
      setSectionStatus((prev) => ({ ...prev, [section.id]: true }));

      // Audit trail: log DELETE action for section deletion
      if (reportTemplateProjectId && !section.fromReportDownload) {
        logAuditAction(reportTemplateProjectId, "DELETE", section.display_name);
      }

      setFeedbackDialogContent(defaultFeedbackContent);
    } catch (error) {
      if (isMissingSectionBookmarkError(error)) {
        message.warning(error.message);
        setFeedbackDialogOpen(false);
        return;
      }

      Logger.error("DialogPopup.tsx: Error submitting feedback:", error);
      setTest(JSON.stringify(error));
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context: axios.isAxiosError(error)
            ? {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                statusText: error.response?.statusText,
                responseData: error.response?.data?.detail,
              }
            : error.name === "RichApi.Error"
            ? {
                errorLocation: error.debugInfo?.errorLocation,
                statement: error.debugInfo?.statement,
                surroundingStatements: error.debugInfo?.surroundingStatements,
              }
            : undefined,
        },
      });
    }
  };

  // Handles the logic for regenerating a section
  const handleSectionRegeneration = async (requestedSection = null) => {
    let uploadedFileInfo = null;
    let appendedAgentRegenerationUpdate = false;
    let regenerationStage = "init";
    const section = requestedSection || regenerateDialogContent.section;
    const sectionId = section?.id;
    const previousOutputDecision = sectionId ? outputDecision?.[sectionId] : undefined;
    let clearedPreviousContent = false;
    let activeTaskId: string | null = null;
    let activeTaskKind: "chat_async" | "agentic" | null = null;
    const activeTaskMode: "regeneration" = "regeneration";
    try {
      if (section?.insertion_key) {
        regenerationStage = "bookmark-preflight";
        const sectionBookmarksExist = await hasSectionBookmarks(section.insertion_key);
        if (!sectionBookmarksExist) {
          throw createMissingSectionBookmarkError(section.display_name);
        }
      }

      setRegenerateLoading(true);
      clearDocumentReadinessNotice();

      const regenerateDialogContentFreeform = regenerateDialogContent?.freeform?.trim()
        ? regenerateDialogContent?.freeform
        : "No feedback comment received";

      let regenerateDialogFeedbackContent = `Jenly-Add-in.Regenerate-Dialog.User-Custom-Instructions: ${regenerateDialogContentFreeform}`;

      if (!section.fromReportDownload) {
        try {
          await submitLangfuseFeedback(
            regenerateDialogContent?.section?.chat_history_id,
            3,
            regenerateDialogFeedbackContent
          );
        } catch (error) {
          Logger.error("DialogPopup.tsx: Error submitting feedback", error);
        }

        await deletePersistedFeedback(regenerateDialogContent?.section?.chat_history_id);
      }

      setFeedbackStates((prev) => {
        const newStates = { ...prev };
        if (sectionId) {
          delete newStates[sectionId];
        }
        return newStates;
      });

      if (sectionId) {
        setOutputDecision((prev) => ({
          ...prev,
          [sectionId]: true,
        }));
      }

      setRegenerateDialogOpen(false);
      setSectionGeneration(true);
      setTimeout(() => {
        scrollToGeneratedSection(sectionId);
      }, 100);

      if (section.insertion_key) {
        regenerationStage = "clear-previous-content";
        await declineChanges(section.insertion_key);
        clearGroundingForSection(section);
        clearedPreviousContent = true;
      }

      const currentDocumentName = currentDocumentUploadName(section.display_name);

      let exec_tag_ids = [];
      if (currentDocumentName) {
        try {
          regenerationStage = "document-upload";
          const results: any = await handleCurrentDocxUpload(selectedProject.id, currentDocumentName);
          if (results && results.documentId) {
            uploadedFileInfo = results;
            results.tags.forEach((item) => exec_tag_ids.push(item.id));
          }
        } catch (error) {
          Logger.error("DialogPopup.tsx: Error uploading current doc during regeneration:", error);
          throw error;
        }
      }

      const combinedTagIds = [...exec_tag_ids];

      if (section.insertion_key) {
        const regenerationInstructionBlock = buildRegenerationInstructionBlock(
          regenerateDialogContent.freeform || "",
          regenerateDialogContent.options || []
        );
        const question = `${section.user_query}${regenerationInstructionBlock}`;

        let llmResponse: any = {};
        const handleChatAsyncProgress = (event) => {
          activeTaskId = event?.task_id || activeTaskId;
          activeTaskKind = "chat_async";
          appendChatAsyncUpdate(section, event);
        };

        if (section.type.toLowerCase() === "section_agentic") {
          const latestSection = generatedSections?.find((s) => s.id === section.id) || section;
          const existingActiveTask = getSectionActiveTask(latestSection);

          appendedAgentRegenerationUpdate = true;
          setGeneratedSections((prevSections) =>
            prevSections.map((s) =>
              s.id === section.id
                ? {
                    ...markSectionRegenerationStarted(s, "agentic"),
                    trace_url: null,
                  }
                : s
            )
          );

          regenerationStage = existingActiveTask?.kind === "agentic" ? "agent-stream" : "agent-query";
          const query =
            existingActiveTask?.kind === "agentic"
              ? { task_id: existingActiveTask.taskId }
              : await retrieveAgentQuery(selectedProject.id, section.id, uploadedFileInfo?.documentId);
          activeTaskId = query.task_id;
          activeTaskKind = "agentic";
          setGeneratedSections((prevSections) =>
            prevSections.map((s) =>
              s.id === section.id
                ? {
                    ...s,
                    trace_url: null,
                    task_id: query.task_id,
                    activeSectionTask: createActiveSectionTask("agentic", "regeneration", query.task_id),
                    agenticRegenerationInProgress: true,
                  }
                : s
            )
          );

          regenerationStage = "agent-stream";
          await handleAgentStream(query, section, llmResponse);
        } else {
          markChatAsyncRegenerationStarted(section);
          regenerationStage = "llm-request";
          llmResponse = await retrievellmOuputWOGroupAsync(
            selectedProject.id,
            null,
            question,
            combinedTagIds,
            section.name,
            undefined,
            handleChatAsyncProgress,
            // Section regeneration: stamp the row as non-interactive
            // so the chat_history row never shows up on the chat with
            // documents page.
            "non_interactive_chat"
          );
        }

        regenerationStage = "process-response";
        if (section.type.toLowerCase() === "section_agentic") {
          const key = `${section.insertion_key}`;
          if (insertionLockRef?.current.has(key)) {
            Logger.debug(
              `DialogPopup.tsx: Skipping agentic regeneration insertion for key ${key} - already locked.`
            );
          } else {
            insertionLockRef?.current.add(key);
            try {
              if (!clearedPreviousContent) {
                regenerationStage = "decline-previous-changes";
                await declineChanges(section.insertion_key);
              }

              regenerationStage = "word-insertion";
              await handleAIOutput(llmResponse, section, true);
            } finally {
              insertionLockRef?.current.delete(key);
            }
          }

          if (sectionId) {
            setOutputDecision((prev) => ({
              ...prev,
              [sectionId]: true,
            }));
          }

          if (reportTemplateProjectId && !section.fromReportDownload) {
            logAuditAction(reportTemplateProjectId, "UPDATE", section.display_name);
          }

          setSelectedSections((prev) => prev.filter((s) => s.id !== section.id));
          setRegenerateDialogContent({ chat_id: "", section: {}, freeform: "", options: [] });
          setRegenerateDialogOpen(false);
          setRegenerateLoading(false);
          setSectionGeneration(false);
          return;
        }

        const output = llmResponse.answer || "No output received";

        section.chat_history_id = llmResponse.id;
        section.trace_url =
          typeof llmResponse?.trace_url === "string" && llmResponse.trace_url.trim().length > 0
            ? llmResponse.trace_url
            : null;

        const sources = llmResponse.sources || [];
        const tagged_document_ids = llmResponse.tagged_document_ids || [];
        for (const docId of tagged_document_ids) {
          const doc = await retreiveSourceDocumentMetadata(docId);
          if (doc) {
            sources.push({
              document: doc.filename,
              file_id: doc.id,
              project_id: selectedProject.id,
              content: "",
            });
          }
        }

        section.sources = sources;
        section.content = output;

        let stateUpdaterForInsert;
        if (section.type.toLowerCase() === "section_agentic") {
          // Update state manually for agentic
          setGeneratedSections((prevSections) =>
            prevSections.map((s) => {
              if (s.id === section.id) {
                const updates = s.agentUpdates || [];
                return {
                  ...s,
                  content: output,
                  sources: sources,
                  chat_history_id: llmResponse.id,
                  trace_url: section.trace_url,
                  activeSectionTask: undefined,
                  task_id: null,
                  agenticInProgress: false,
                  agenticRegenerationInProgress: false,
                  agentUpdates: updates,
                };
              }
              return s;
            })
          );
          // Don't let insertAtSection update state
          stateUpdaterForInsert = () => {};
        } else {
          // Update state manually for chat-async
          setGeneratedSections((prevSections) =>
            prevSections.map((generatedSection) => {
              if (generatedSection.id !== section.id) return generatedSection;
              return {
                ...generatedSection,
                content: output,
                sources: sources,
                chat_history_id: llmResponse.id,
                trace_url: section.trace_url,
                chatAsyncInProgress: false,
                chatAsyncRegenerationInProgress: false,
                activeSectionTask: undefined,
                evalStatus: undefined,
              };
            })
          );
          // Don't let insertAtSection update state
          stateUpdaterForInsert = () => {};
        }

        regenerationStage = "prepare-content";
        const key = `${section.insertion_key}`;
        if (insertionLockRef?.current.has(key)) {
          Logger.debug(
            `DialogPopup.tsx: Skipping declineChanges and insertAtSection for key ${key} - reconnection already handled.`
          );
        } else {
          insertionLockRef?.current.add(key);
          try {
            regenerationStage = "word-insertion";
            await renderSectionContent({
              markdown: output, section, projectId: selectedProject?.id, draftStyles,
              insert: section.fromReportDownload ? undefined : insertAtSection,
              beforeInsert: async () => {
                if (!clearedPreviousContent) {
                  regenerationStage = "decline-previous-changes";
                  await declineChanges(section.insertion_key);
                }
                regenerationStage = "word-insertion";
              },
              retrieveDocumentsForImageInsertion, retrieveDocumentImages, downloadDocumentImage,
              setGeneratedSections: stateUpdaterForInsert, setSectionStatus,
            });
          } finally {
            insertionLockRef?.current.delete(key);
          }
        }

        if (sectionId) {
          setOutputDecision((prev) => ({
            ...prev,
            [sectionId]: true,
          }));
        }

        // Audit trail: log UPDATE action for section regeneration
        if (reportTemplateProjectId && !section.fromReportDownload) {
          logAuditAction(reportTemplateProjectId, "UPDATE", section.display_name);
        }

        setSelectedSections((prev) => prev.filter((s) => s.id !== section.id));
      }

      setRegenerateDialogContent({ chat_id: "", section: {}, freeform: "", options: [] });
      setRegenerateDialogOpen(false);
      setRegenerateLoading(false);
      setSectionGeneration(false);
    } catch (error: any) {
      Logger.error(
        `DialogPopup.tsx: Section "${section?.display_name}" regeneration failed at stage "${regenerationStage}":`,
        error,
        {
          stage: regenerationStage,
          sectionId: section?.id,
          sectionType: section?.type,
          status: error?.response?.status,
        }
      );
      if (isMissingSectionBookmarkError(error)) {
        message.warning(error.message);
        if (sectionId) {
          setSectionStatus((prev) => ({ ...prev, [sectionId]: false }));
        }
        markChatAsyncRegenerationFinished(section);
        setOutputDecision((prev) => {
          const next = { ...prev };

          if (sectionId) {
            if (typeof previousOutputDecision === "undefined") {
              delete next[sectionId];
            } else {
              next[sectionId] = previousOutputDecision;
            }
          }

          return next;
        });
        setRegenerateDialogOpen(false);
        setRegenerateLoading(false);
        setSectionGeneration(false);
        return;
      }

      const readinessNotice = getDocumentsNotReadyPayload(error);
      const activeSectionTask = createActiveSectionTask(activeTaskKind as any, activeTaskMode, activeTaskId);

      if (
        activeTaskKind === "agentic" &&
        (regenerationStage === "agent-stream" || error?.agenticTerminalFailure || error?.agenticReconnectExhausted)
      ) {
        const responseDetail = error.response?.data?.detail?.trim?.() || error.message?.trim?.() || "";
        releaseAgenticRegenerationFailure(
          section,
          responseDetail || "Agentic section regeneration failed. Please try again.",
          activeTaskId
        );
        setRegenerateDialogOpen(false);
        setRegenerateLoading(false);
        setSectionGeneration(false);
        return;
      }

      if (activeSectionTask && isRecoverableTaskInterruption(error, { ...section, activeSectionTask })) {
        setGeneratedSections((prevSections) =>
          prevSections.map((generatedSection) =>
            generatedSection.id === section.id
              ? {
                  ...generatedSection,
                  activeSectionTask: generatedSection.activeSectionTask || activeSectionTask,
                  chat_async_task_id:
                    activeSectionTask.kind === "chat_async"
                      ? activeSectionTask.taskId
                      : generatedSection.chat_async_task_id,
                  chatAsyncInProgress:
                    activeSectionTask.kind === "chat_async" ? true : generatedSection.chatAsyncInProgress,
                  chatAsyncRegenerationInProgress:
                    activeSectionTask.kind === "chat_async" ? true : generatedSection.chatAsyncRegenerationInProgress,
                  task_id: activeSectionTask.kind === "agentic" ? activeSectionTask.taskId : generatedSection.task_id,
                  agenticRegenerationInProgress:
                    activeSectionTask.kind === "agentic" ? true : generatedSection.agenticRegenerationInProgress,
                  agentUpdates: appendTaskUpdateIfNew(generatedSection.agentUpdates || [], {
                    message: "Connection interrupted. Waiting to reconnect...",
                    status: "running",
                    task_id: activeSectionTask.taskId,
                  }),
                }
              : generatedSection
          )
        );
        setRegenerateDialogOpen(false);
        setRegenerateLoading(false);
        setSectionGeneration(false);
        return;
      }

      markChatAsyncRegenerationFinished(section);

      if (readinessNotice) {
        if (appendedAgentRegenerationUpdate) {
          setGeneratedSections((prevSections) =>
            prevSections.map((generatedSection) => {
              if (generatedSection.id !== section.id) {
                return generatedSection;
              }

              const nextUpdates = [...(generatedSection.agentUpdates || [])];
              const lastUpdate = nextUpdates[nextUpdates.length - 1];

              if (lastUpdate?.message === "Regenerating agent output..." && lastUpdate?.status === "pending") {
                nextUpdates.pop();
              }

              return {
                ...generatedSection,
                agentUpdates: nextUpdates,
              };
            })
          );
        }

        setOutputDecision((prev) => {
          const next = { ...prev };

          if (sectionId) {
            if (typeof previousOutputDecision === "undefined") {
              delete next[sectionId];
            } else {
              next[sectionId] = previousOutputDecision;
            }
          }

          return next;
        });
        setDocumentReadinessNotice(readinessNotice);
        setRegenerateDialogOpen(false);
        setRegenerateLoading(false);
        setSectionGeneration(false);
        return;
      }

      const responseDetail = error.response?.data?.detail?.trim?.() || error.message?.trim?.() || "";
      const errorMessage = `TODO: Section re-generation failed (stage: ${regenerationStage}): An unexpected error occurred during generation. Please try again in a few moments, or please notify Jenly.ai if the issue persists.${
        responseDetail ? ` ${responseDetail}` : ""
      }`;

      Logger.error(`DialogPopup.tsx: ${errorMessage}`, { sectionId: section?.id, sectionName: section?.display_name });

      const itemArray = await prepareSectionContentForInsertion({
        markdown: errorMessage,
        parseMarkdownSections,
        projectId: selectedProject?.id,
        retrieveDocumentsForImageInsertion,
        retrieveDocumentImages,
        downloadDocumentImage,
      });

      await declineChanges(regenerateDialogContent?.section?.insertion_key);

      if (regenerateDialogContent?.section?.insertion_key) {
        await insertAtSection(
          `${regenerateDialogContent.section.insertion_key}`,
          itemArray,
          500000,
          setGeneratedSections,
          setSectionStatus,
          regenerateDialogContent.section,
          draftStyles
        );
      }

      setRegenerateDialogOpen(false);
      setRegenerateLoading(false);
      setSectionGeneration(false);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context: axios.isAxiosError(error)
            ? {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                statusText: error.response?.statusText,
                responseData: error.response?.data?.detail,
              }
            : error.name === "RichApi.Error"
            ? {
                errorLocation: error.debugInfo?.errorLocation,
                statement: error.debugInfo?.statement,
                surroundingStatements: error.debugInfo?.surroundingStatements,
              }
            : undefined,
        },
      });
    } finally {
      if (uploadedFileInfo) {
        try {
          Logger.debug("DialogPopup.tsx: Cleaning up temporary regeneration files...");
          await deleteDocument(uploadedFileInfo.documentId);

          if (uploadedFileInfo.tags && uploadedFileInfo.tags.length > 0) {
            const tagsToDelete = uploadedFileInfo.tags.filter((tag) => tag.name && tag.name.startsWith("tmp-"));
            await Promise.all(tagsToDelete.map((tag) => deleteTag(tag.id)));
            Logger.debug(`DialogPopup.tsx: Deleted ${tagsToDelete.length} temporary tags.`);
          }

          Logger.debug("DialogPopup.tsx: Regeneration cleanup successful.");
        } catch (cleanupError) {
          Logger.error("DialogPopup.tsx: Failed to clean up temporary regeneration docs:", cleanupError);
        }
      }
      setRegenerateLoading(false);
      setSectionGeneration(false);
    }
  };

  // Handles the logic for closing the regeneration dialog
  const handleRegenerateDialogClose = () => {
    setRegenerateDialogContent({ chat_id: "", section: {}, freeform: "", options: [] });
    setRegenerateDialogOpen(false);
  };

  // Toggles the expansion state of a source document
  const toggleSourceExpansion = (index: number) => {
    const newExpanded = new Set(expandedSources);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSources(newExpanded);
  };

  // Copies message to clipboad
  function navigator_clipboard_writeText(text: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const textArea = document.createElement("textarea") as HTMLTextAreaElement;
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (successful) {
          resolve();
        } else {
          reject(new Error("execCommand('copy') failed"));
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  // Copies the content of a source to the clipboard and sets the copied state
  const copySourceContent = async (content: string, index: number) => {
    try {
      if (
        Office.context.diagnostics.platform !== Office.PlatformType.OfficeOnline &&
        navigator.clipboard &&
        navigator.clipboard.writeText
      ) {
        // modern API
        await navigator.clipboard.writeText(content);
      } else {
        // fallback
        await navigator_clipboard_writeText(content);
      }
      setCopiedSource(index);
      setTimeout(() => setCopiedSource(null), 2000);
    } catch (error) {
      Logger.error("DialogPopup.tsx: Failed to copy content:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context: undefined,
        },
      });
    }
  };

  // Generates a preview text for the source content
  const getPreviewText = (content: string, maxLength = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + "...";
  };

  // Fixes spacing issues in the source content
  const fixSpacing = (text: string) => {
    const formattedText = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "")
      .map((line) => line.replace(/\s{2,}/g, " "))
      .join("\n")
      .trim();
    return formattedText;
  };

  function startDownloadInBrowser({ filename, content_type, file_data }) {
    // const popup = Office.context.ui.openBrowserWindow(`${window.location.origin}/download.html`);

    const popup = window.open(`${window.location.origin}/download.html`, "_blank");

    // Delay postMessage until popup loads
    const interval = setInterval(() => {
      if (!popup) return;

      try {
        popup.postMessage({ file_data, filename, content_type }, window.location.origin);
        clearInterval(interval);
      } catch (e) {
        Logger.debug("DialogPopup.tsx: Waiting for popup to be ready...");
      }
    }, 500);
  }

  // Retrieves a source document by its ID and updates the dialog content with the file data
  const downloadFile = async (file_id: string) => {
    try {
      const file_data = await retrieveSourceDocument(file_id);

      setSourceDialogContent((prev) =>
        prev.map((item) => (item.file_id === file_id ? { ...item, file_info: file_data } : item))
      );
      downloadAndOpenBase64File(file_data);
    } catch (error) {
      Logger.error("DialogPopup.tsx: Error retrieving or downloading source document:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context: axios.isAxiosError(error)
            ? {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                statusText: error.response?.statusText,
                responseData: error.response?.data?.detail,
              }
            : undefined,
        },
      });
    }
  };

  return (
    <>
      {(sourceDialogOpen || feedbackDialogOpen || regenerateDialogOpen || qualityCheckDialogOpen) && (
        <div className="dialog-popup-overlay">
          <div className="dialog-popup-container">
            <DialogPopupHeader sectionDialogContent={sectionDialogContent} dialogPopupTitle={dialogPopupTitle}>
              {sourceDialogOpen && <ReadOutlined />}
              {feedbackDialogOpen && <CommentOutlined />}
              {regenerateDialogOpen && <SyncOutlined rotate={160} />}
              {qualityCheckDialogOpen && <FileDoneOutlined />}
            </DialogPopupHeader>
            <div ref={containerRef} className="dialog-popup-content">
              {regenerateDialogOpen && (
                <div className="dialog-popup-form">
                  <DialogPopupToggleOptions
                    toggleOptions={toggleOptions}
                    regenerateDialogContent={regenerateDialogContent}
                    toggleRegenerateOptions={toggleRegenerateOptions}
                    onChangeToggleState={onChangeToggleState}
                    checkOptions={checkOptions}
                  />
                  <DialogPopupOptions
                    regenerateDialogContent={regenerateDialogContent}
                    setRegenerateDialogContent={setRegenerateDialogContent}
                  />
                  <DialopPopupCustomInstructions
                    setRegenerateDialogContent={setRegenerateDialogContent}
                    regenerateDialogContent={regenerateDialogContent}
                  />
                  <DialogPopupProjectDocuments selectedProject={selectedProject} />
                </div>
              )}
              <DialogPopupFeedback
                feedbackDialogOpen={feedbackDialogOpen}
                setFeedbackDialogContent={setFeedbackDialogContent}
                feedbackDialogContent={feedbackDialogContent}
              />
              <DialogPopupSourceDocuments
                sourceDialogOpen={sourceDialogOpen}
                sourceDialogContent={sourceDialogContent}
                expandedSources={expandedSources}
                copiedSource={copiedSource}
                getPreviewText={getPreviewText}
                downloadFile={downloadFile}
                location={location}
                copySourceContent={copySourceContent}
                toggleSourceExpansion={toggleSourceExpansion}
                fixSpacing={fixSpacing}
              />
              {qualityCheckDialogOpen && (
                <div className="dialog-popup-section">
                  <QualityCheckDisplay qualityCheckDialogContent={qualityCheckDialogContent} />
                </div>
              )}
            </div>
            <DialogPopupFooter
              feedbackDialogOpen={feedbackDialogOpen}
              regenerateLoading={regenerateLoading}
              setFeedbackDialogOpen={setFeedbackDialogOpen}
              handleDelete={handleDelete}
              regenerateDialogOpen={regenerateDialogOpen}
              handleRegenerateDialogClose={handleRegenerateDialogClose}
              handleSectionRegeneration={handleSectionRegeneration}
              sourceDialogOpen={sourceDialogOpen}
              setSourceDialogOpen={setSourceDialogOpen}
              qualityCheckDialogOpen={qualityCheckDialogOpen}
              setQualityCheckDialogOpen={setQualityCheckDialogOpen}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default DialogPopup;
